<script setup>
import { ref, computed, watch } from 'vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import Alert from '@/components/Atoms/Alert.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconLock from '@/components/icon/IconLock.vue'
import SyncWordsSettingsFields from './SyncWordsSettingsFields.vue'
import {
	FALLBACK_ENGINE_INFO,
	draftFromFlow,
	draftKey,
	settingsFromDraft,
	validateDraft,
	mapServerErrors,
	unsupportedLanguageErrors,
	PROVIDER_ROUTE,
	PROVIDER_FLOW_CODES,
} from './captionSyncWords'
import { UPGRADE_ROUTE, DEFAULT_LIMITS } from './captionLimits'
import { track, EVENTS } from '@/lib/tracking'

/**
 * Settings card for a SyncWords flow: the one-language settings form and
 * Save. Start lives in the go-live card and sends unsaved changes with it, so
 * the draft is reported as `pending` ({ dirty, valid, settings }). Core locks
 * the settings while captions run (409 `captions_settings_locked`).
 */
const props = defineProps({
	flow: { type: Object, required: true },
	info: { type: Object, default: () => FALLBACK_ENGINE_INFO },
	saving: { type: Boolean, default: false },
	starting: { type: Boolean, default: false },
	// { status, code, message, action, errors?, upgrade? } from the last failed save/start, or null.
	serverError: { type: Object, default: null },
	// Caption window from `windowOf(status, flow)`.
	window: { type: Object, default: () => ({ state: 'idle', durationSec: null, expiresAt: null }) },
	// Plan limits (captionLimits.js): `maxLangs` caps the flow's languages, spoken one included.
	limits: { type: Object, default: () => ({ ...DEFAULT_LIMITS }) },
	disabled: { type: Boolean, default: false },
})

const emit = defineEmits(['save', 'pending', 'stop'])

const running = computed(() => props.window?.state === 'running')
const locked = computed(() => running.value || props.disabled)

const saved = computed(() => draftFromFlow(props.flow, props.info))
const draft = ref(saved.value)
const savedKey = computed(() => draftKey(saved.value))
watch(savedKey, () => { draft.value = saved.value })
function reset() {
	draft.value = saved.value
}

const dirty = computed(() => draftKey(draft.value) !== savedKey.value)
const maxLangs = computed(() => (Number.isInteger(props.limits?.maxLangs) && props.limits.maxLangs > 0 ? props.limits.maxLangs : DEFAULT_LIMITS.maxLangs))
// Below the service's own cap, the plan is what limits the languages.
const planCapped = computed(() => props.limits?.enforced === true && maxLangs.value < DEFAULT_LIMITS.maxLangs)
const validation = computed(() => validateDraft(draft.value, props.info, { maxLangs: maxLangs.value }))

// Core's field errors, kept with the value they were raised on so editing the field clears them.
const serverFields = ref({})
const serverGeneral = ref([])
const FIELD_CODES = ['captions_invalid_settings']
const LANGUAGES_LIMIT = 'captions_too_many_languages'
// Teletext can't show the language's script: core's message goes on that field.
const UNSUPPORTED_LANGUAGE = 'captions_unsupported_caption_language'
watch(() => props.serverError, (err) => {
	const unsupported = unsupportedLanguageErrors(err, draft.value)
	if (unsupported) {
		serverFields.value = Object.fromEntries(Object.entries(unsupported).map(([field, message]) => [field, { message, value: draft.value[field] }]))
		serverGeneral.value = []
		return
	}
	if (err?.code === LANGUAGES_LIMIT) {
		serverFields.value = { translations: { message: err.message, value: draft.value.translations } }
		serverGeneral.value = []
		return
	}
	if (!err || !FIELD_CODES.includes(err.code) || !Array.isArray(err.errors)) {
		serverFields.value = {}
		serverGeneral.value = []
		return
	}
	const { fields, general } = mapServerErrors(err.errors)
	serverFields.value = Object.fromEntries(Object.entries(fields).map(([field, message]) => [field, { message, value: draft.value[field] }]))
	serverGeneral.value = general
})
const errors = computed(() => {
	const out = {}
	for (const [field, e] of Object.entries(serverFields.value)) {
		if (draft.value[field] === e.value) out[field] = e.message
	}
	return { ...out, ...validation.value.errors }
})
const hasFieldErrors = computed(() => Object.keys(errors.value).length > 0)
const languagesUpgrade = computed(() => props.serverError?.code === LANGUAGES_LIMIT && !!props.serverError.upgrade && !!errors.value.translations)

const canSave = computed(() => dirty.value && validation.value.valid && !props.saving && !props.starting && !locked.value)

function save() {
	if (!canSave.value) return
	emit('save', settingsFromDraft(draft.value))
}

watch(() => [dirty.value, validation.value.valid, draftKey(draft.value)], () => {
	emit('pending', { dirty: dirty.value, valid: validation.value.valid, settings: dirty.value ? settingsFromDraft(draft.value) : null })
}, { immediate: true })

// Start failures show on the go-live card; field errors show here for both.
const saveError = computed(() => (props.serverError?.action === 'start' ? null : props.serverError))
// A gateway status without a code is the service being unreachable; coded 502s carry their own copy.
const unreachable = computed(() => saveError.value && [502, 503, 504].includes(saveError.value.status) && !saveError.value.code)
const fieldErrorAlert = computed(() => props.serverError && FIELD_CODES.includes(props.serverError.code))
// The language limit shows inline under Caption languages, not as an alert.
const genericError = computed(() => saveError.value && ![LANGUAGES_LIMIT, UNSUPPORTED_LANGUAGE].includes(saveError.value.code) && !unreachable.value && !fieldErrorAlert.value)
const providerError = computed(() => PROVIDER_FLOW_CODES.includes(saveError.value?.code))
</script>

<template>
	<section class="flex flex-col gap-5 rounded-xl bg-surface-2 p-4 sm:p-5" aria-labelledby="sw-settings-heading" data-testid="syncwords-settings-panel">
		<header class="flex items-center justify-between gap-2">
			<h2 id="sw-settings-heading" class="m-0 text-base font-semibold text-surface-text-2">Caption settings</h2>
			<span class="rounded-full bg-surface-3 px-2 py-0.5 text-[11px] font-medium text-surface-8" title="Each caption language is its own Teletext subtitle page (DVB-TXT) in the stream">Teletext subtitles</span>
		</header>

		<div v-if="running" class="flex flex-wrap items-center gap-2 rounded-lg bg-surface-3 px-3 py-2" role="note" data-testid="settings-locked">
			<icon-base width="12" height="12" fill="none" class="shrink-0 text-surface-7"><icon-lock /></icon-base>
			<span class="min-w-0 flex-1 text-xs text-surface-8">Settings are locked while captions run. Stop captions to change them.</span>
			<FormButton type="secondary" size="sm" class="shrink-0" data-testid="stop-to-edit" @click="emit('stop')">Stop to edit</FormButton>
		</div>

		<SyncWordsSettingsFields
			v-model="draft"
			:info="info"
			:errors="errors"
			:disabled="locked"
			:max-langs="maxLangs"
			:plan-capped="planCapped"
			:languages-upgrade="languagesUpgrade"
			:pages="flow.syncwords?.pages || []"
			id-prefix="sw-manage"
		/>

		<Alert v-if="unreachable" type="errwarning" role="alert">
			The captioning service isn't reachable right now. Try again.
		</Alert>
		<Alert v-else-if="fieldErrorAlert" type="error" role="alert" :data-error-code="serverError.code">
			<span class="flex flex-col gap-1">
				<span>Some settings need fixing before they can be saved.</span>
				<span v-for="(msg, i) in serverGeneral" :key="i" class="text-xs">{{ msg }}</span>
			</span>
		</Alert>
		<Alert v-else-if="genericError" type="error" role="alert" :data-error-code="saveError.code || undefined" :data-vendor-name="providerError ? '' : undefined">
			<span class="flex w-full flex-wrap items-center gap-x-1 gap-y-1">
				<span>{{ saveError.message }}</span>
				<router-link v-if="providerError" :to="PROVIDER_ROUTE" class="font-medium text-white underline">Reconnect</router-link>
				<router-link v-if="saveError.upgrade" :to="UPGRADE_ROUTE" class="font-medium text-white underline" @click="track(EVENTS.UPGRADE_CLICKED, { source: 'captioning-limit' })">
					Upgrade
				</router-link>
			</span>
		</Alert>

		<footer v-if="!running && dirty" class="mt-auto flex flex-col gap-2 border-t border-surface-3 pt-4 sm:flex-row sm:items-center sm:justify-end">
			<span v-if="!hasFieldErrors" class="text-xs text-surface-7 sm:mr-auto" role="note" data-testid="unsaved-note-settings">Unsaved changes are used when you start captions.</span>
			<FormButton type="secondary" size="md" class="justify-center" :disabled="saving || starting" @click="reset">Reset</FormButton>
			<FormButton type="main" size="md" class="justify-center" :disabled="!canSave" :is-loading="saving" @click="save">Save</FormButton>
		</footer>
	</section>
</template>
